import { toast } from "sonner";
import { DEMO_USER_ID } from "./demoMode";

type Row = Record<string, any>;
export type DemoTables = Record<string, Row[]>;
/** Holder so the seed can load after the client is created (see demoLoader). */
export interface DemoStore {
  tables: DemoTables;
  /** Canned responses for edge functions that return data the page needs. */
  functions?: DemoFunctionHandlers;
}
export type DemoFunctionHandlers = Record<string, (body: any) => unknown>;

const READ_ONLY_MESSAGE = "This is a read-only demo. Start a free trial to make changes.";

const readOnlyError = () => ({
  message: READ_ONLY_MESSAGE,
  details: "",
  hint: "",
  code: "DEMO_READ_ONLY",
});

let lastNoticeAt = 0;
const loadedAt = Date.now();
const notifyReadOnly = () => {
  const now = Date.now();
  // Pages fire housekeeping writes on load; only surface the notice once the
  // visitor has had a chance to click something.
  if (now - loadedAt < 4000 || now - lastNoticeAt < 2500) return;
  lastNoticeAt = now;
  toast.info("Read-only demo", { description: "Start a free trial to make changes." });
};

// ---------------------------------------------------------------------------
// Column / filter helpers
// ---------------------------------------------------------------------------

const getValue = (row: Row, column: string): any => {
  if (!column.includes("->") && !(column in row) && column.includes(".")) {
    // Filter on an embedded relation, e.g. "listings.business_id".
    const [head, ...rest] = column.split(".");
    let current: any = row[head];
    if (Array.isArray(current)) current = current[0];
    for (const key of rest) {
      if (current == null) return null;
      current = current[key];
    }
    return current ?? null;
  }
  if (column.includes("->")) {
    const [head, ...rest] = column.split(/->>?/);
    let current: any = row[head];
    for (const key of rest) {
      if (current == null) return null;
      if (typeof current === "string") {
        try { current = JSON.parse(current); } catch { return null; }
      }
      current = current[key];
    }
    return current ?? null;
  }
  return row[column];
};

const parseLiteral = (raw: string): any => {
  if (raw === "null") return null;
  if (raw === "true") return true;
  if (raw === "false") return false;
  return raw;
};

const looseEquals = (a: any, b: any) => {
  if (a == null || b == null) return a == b;
  return String(a) === String(b);
};

const compare = (a: any, b: any) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  const na = Number(a), nb = Number(b);
  if (typeof a !== "string" || (!Number.isNaN(na) && !Number.isNaN(nb) && String(na) === a && String(nb) === b)) {
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  }
  return String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0;
};

const likeToRegExp = (pattern: string, flags: string) =>
  new RegExp(
    "^" + pattern.replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/%/g, ".*").replace(/_/g, ".") + "$",
    flags,
  );

const asArray = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }
  return [];
};

const splitTopLevel = (input: string): string[] => {
  const parts: string[] = [];
  let depth = 0;
  let current = "";
  for (const char of input) {
    if (char === "(") depth++;
    if (char === ")") depth--;
    if (char === "," && depth === 0) {
      parts.push(current);
      current = "";
    } else current += char;
  }
  if (current.trim()) parts.push(current);
  return parts.map((part) => part.trim()).filter(Boolean);
};

type Predicate = (row: Row) => boolean;

const buildPredicate = (column: string, operator: string, rawValue: any): Predicate => {
  switch (operator) {
    case "eq": return (r) => looseEquals(getValue(r, column), rawValue);
    case "neq": return (r) => !looseEquals(getValue(r, column), rawValue);
    case "gt": return (r) => compare(getValue(r, column), rawValue) > 0 && getValue(r, column) != null;
    case "gte": return (r) => compare(getValue(r, column), rawValue) >= 0 && getValue(r, column) != null;
    case "lt": return (r) => compare(getValue(r, column), rawValue) < 0 && getValue(r, column) != null;
    case "lte": return (r) => compare(getValue(r, column), rawValue) <= 0 && getValue(r, column) != null;
    case "like": return (r) => likeToRegExp(String(rawValue), "").test(String(getValue(r, column) ?? ""));
    case "ilike": return (r) => likeToRegExp(String(rawValue), "i").test(String(getValue(r, column) ?? ""));
    case "is": {
      const target = typeof rawValue === "string" ? parseLiteral(rawValue) : rawValue;
      return (r) => (target === null ? getValue(r, column) == null : getValue(r, column) === target);
    }
    case "in": {
      const list: any[] = Array.isArray(rawValue)
        ? rawValue
        : String(rawValue).replace(/^\(|\)$/g, "").split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
      return (r) => list.some((v) => looseEquals(getValue(r, column), v));
    }
    case "cs":
    case "contains": {
      return (r) => {
        const cell = getValue(r, column);
        if (Array.isArray(rawValue) || typeof rawValue === "string") {
          const needles = Array.isArray(rawValue)
            ? rawValue
            : String(rawValue).replace(/^[{[]|[}\]]$/g, "").split(",").map((v) => v.trim()).filter(Boolean);
          const hay = asArray(cell);
          return needles.every((n) => hay.some((h) => looseEquals(h, n)));
        }
        if (rawValue && typeof rawValue === "object") {
          const obj = typeof cell === "string" ? safeParse(cell) : cell;
          return !!obj && Object.entries(rawValue).every(([k, v]) => looseEquals(obj[k], v));
        }
        return false;
      };
    }
    case "ov":
    case "overlaps": {
      const needles = Array.isArray(rawValue) ? rawValue : [];
      return (r) => asArray(getValue(r, column)).some((h) => needles.some((n) => looseEquals(h, n)));
    }
    default:
      return () => true;
  }
};

const safeParse = (value: string) => {
  try { return JSON.parse(value); } catch { return null; }
};

const parseOrExpression = (expression: string): Predicate => {
  const clauses = splitTopLevel(expression).map((clause): Predicate => {
    const andMatch = clause.match(/^and\((.*)\)$/);
    if (andMatch) {
      const inner = splitTopLevel(andMatch[1]).map((c) => parseOrExpression(c));
      return (r) => inner.every((p) => p(r));
    }
    const negated = clause.match(/^([^.]+)\.not\.([a-z]+)\.(.*)$/);
    if (negated) {
      const p = buildPredicate(negated[1], negated[2], negated[3]);
      return (r) => !p(r);
    }
    const match = clause.match(/^([^.]+)\.([a-z]+)\.(.*)$/);
    if (!match) return () => true;
    return buildPredicate(match[1], match[2], match[3]);
  });
  return (r) => clauses.some((p) => p(r));
};

// ---------------------------------------------------------------------------
// Select-list parsing and relation embedding
// ---------------------------------------------------------------------------

type SelectItem =
  | { kind: "star" }
  | { kind: "column"; name: string; alias: string }
  | { kind: "embed"; alias: string; table: string; inner: boolean; items: SelectItem[] };

const parseSelect = (input: string): SelectItem[] =>
  splitTopLevel(input).map((part): SelectItem => {
    if (part === "*") return { kind: "star" };
    const paren = part.indexOf("(");
    if (paren === -1) {
      const [alias, name] = part.includes(":") ? part.split(":") : [part, part];
      return { kind: "column", name: name.replace(/::.*$/, ""), alias: alias.replace(/::.*$/, "") };
    }
    const head = part.slice(0, paren);
    const inside = part.slice(paren + 1, part.lastIndexOf(")"));
    const [aliasPart, rest] = head.includes(":") ? head.split(":") : [null, head];
    const [table, ...hints] = rest.split("!");
    return {
      kind: "embed",
      alias: aliasPart ?? table,
      table,
      inner: hints.includes("inner"),
      items: parseSelect(inside),
    };
  });

const singular = (name: string) =>
  name.endsWith("ies") ? `${name.slice(0, -3)}y` : name.endsWith("s") ? name.slice(0, -1) : name;

const TABLE_KEYS: Record<string, string> = { contacts: "contact_id", listings: "listing_id" };

const project = (row: Row, items: SelectItem[], tables: DemoTables, parentTable: string): Row | null => {
  const out: Row = {};
  for (const item of items) {
    if (item.kind === "star") Object.assign(out, row);
    else if (item.kind === "column") out[item.alias] = getValue(row, item.name);
    else {
      const related = resolveEmbed(row, parentTable, item.table, tables);
      const projected = related
        .map((r) => project(r, item.items, tables, item.table))
        .filter((r): r is Row => r !== null);
      const toOne = isToOne(row, parentTable, item.table);
      if (item.inner && projected.length === 0) return null;
      out[item.alias] = toOne ? projected[0] ?? null : projected;
    }
  }
  return out;
};

const isToOne = (row: Row, _parent: string, target: string) => `${singular(target)}_id` in row;

const resolveEmbed = (row: Row, parent: string, target: string, tables: DemoTables): Row[] => {
  const targetRows = tables[target] ?? [];
  const fk = `${singular(target)}_id`;
  if (fk in row) {
    const key = TABLE_KEYS[target] ?? fk;
    return targetRows.filter((t) => looseEquals(t[key] ?? t.id, row[fk]) && row[fk] != null);
  }
  const parentFk = `${singular(parent)}_id`;
  const parentKey = TABLE_KEYS[parent] ?? parentFk;
  return targetRows.filter((t) => t[parentFk] != null && looseEquals(t[parentFk], row[parentKey] ?? row.id));
};

// ---------------------------------------------------------------------------
// Query builder
// ---------------------------------------------------------------------------

type Result = { data: any; error: any; count: number | null; status: number; statusText: string };

class DemoQuery implements PromiseLike<Result> {
  private predicates: Predicate[] = [];
  private selectItems: SelectItem[] = [{ kind: "star" }];
  private orders: Array<{ column: string; ascending: boolean; nullsFirst?: boolean }> = [];
  private limitCount: number | null = null;
  private rangeFrom: number | null = null;
  private rangeTo: number | null = null;
  private mode: "many" | "single" | "maybe" = "many";
  private wantCount = false;
  private headOnly = false;
  private isWrite = false;

  constructor(private readonly table: string, private readonly store: DemoStore) {}

  private get tables(): DemoTables {
    return this.store.tables;
  }

  select(columns = "*", options?: { count?: string; head?: boolean }) {
    if (!this.isWrite) this.selectItems = parseSelect(columns);
    if (options?.count) this.wantCount = true;
    if (options?.head) this.headOnly = true;
    return this;
  }
  insert() { this.isWrite = true; return this; }
  update() { this.isWrite = true; return this; }
  upsert() { this.isWrite = true; return this; }
  delete() { this.isWrite = true; return this; }

  private add(column: string, operator: string, value: any) {
    this.predicates.push(buildPredicate(column, operator, value));
    return this;
  }
  eq(c: string, v: any) { return this.add(c, "eq", v); }
  neq(c: string, v: any) { return this.add(c, "neq", v); }
  gt(c: string, v: any) { return this.add(c, "gt", v); }
  gte(c: string, v: any) { return this.add(c, "gte", v); }
  lt(c: string, v: any) { return this.add(c, "lt", v); }
  lte(c: string, v: any) { return this.add(c, "lte", v); }
  like(c: string, v: any) { return this.add(c, "like", v); }
  ilike(c: string, v: any) { return this.add(c, "ilike", v); }
  is(c: string, v: any) { return this.add(c, "is", v); }
  in(c: string, v: any[]) { return this.add(c, "in", v); }
  contains(c: string, v: any) { return this.add(c, "contains", v); }
  overlaps(c: string, v: any) { return this.add(c, "overlaps", v); }
  match(query: Record<string, any>) {
    Object.entries(query).forEach(([c, v]) => this.add(c, "eq", v));
    return this;
  }
  not(column: string, operator: string, value: any) {
    const predicate = buildPredicate(column, operator, value);
    this.predicates.push((r) => !predicate(r));
    return this;
  }
  or(expression: string) {
    this.predicates.push(parseOrExpression(expression));
    return this;
  }
  filter(column: string, operator: string, value: any) { return this.add(column, operator, value); }
  textSearch() { return this; }
  order(column: string, options?: { ascending?: boolean; nullsFirst?: boolean }) {
    this.orders.push({ column, ascending: options?.ascending ?? true, nullsFirst: options?.nullsFirst });
    return this;
  }
  limit(count: number) { this.limitCount = count; return this; }
  range(from: number, to: number) { this.rangeFrom = from; this.rangeTo = to; return this; }
  single() { this.mode = "single"; return this; }
  maybeSingle() { this.mode = "maybe"; return this; }
  abortSignal() { return this; }
  returns() { return this; }
  throwOnError() { return this; }

  private run(): Result {
    if (this.isWrite) {
      notifyReadOnly();
      return { data: null, error: readOnlyError(), count: null, status: 403, statusText: "Forbidden" };
    }
    const embeds = this.selectItems.filter(
      (item): item is Extract<SelectItem, { kind: "embed" }> => item.kind === "embed",
    );
    // Filters may reference embedded relations ("listings.business_id"), so
    // evaluate them against the row plus its joined rows.
    const augment = (row: Row): Row => {
      if (!embeds.length) return row;
      const joined: Row = {};
      for (const embed of embeds) {
        const related = resolveEmbed(row, this.table, embed.table, this.tables);
        joined[embed.alias] = isToOne(row, this.table, embed.table) ? related[0] ?? null : related;
      }
      return { ...row, ...joined };
    };
    let rows = (this.tables[this.table] ?? []).filter((row) => {
      const candidate = augment(row);
      return this.predicates.every((p) => p(candidate));
    });
    const total = rows.length;

    if (this.orders.length) {
      rows = [...rows].sort((a, b) => {
        for (const { column, ascending, nullsFirst } of this.orders) {
          const av = getValue(a, column), bv = getValue(b, column);
          if (av == null || bv == null) {
            if (av == null && bv == null) continue;
            const nullsLast = nullsFirst === undefined ? ascending : !nullsFirst;
            return (av == null ? 1 : -1) * (nullsLast ? 1 : -1);
          }
          const result = compare(av, bv);
          if (result !== 0) return ascending ? result : -result;
        }
        return 0;
      });
    }
    if (this.rangeFrom !== null && this.rangeTo !== null) rows = rows.slice(this.rangeFrom, this.rangeTo + 1);
    if (this.limitCount !== null) rows = rows.slice(0, this.limitCount);

    const count = this.wantCount ? total : null;
    if (this.headOnly) return { data: null, error: null, count, status: 200, statusText: "OK" };

    const projected = rows
      .map((row) => project(row, this.selectItems, this.tables, this.table))
      .filter((r): r is Row => r !== null);

    if (this.mode === "many") return { data: projected, error: null, count, status: 200, statusText: "OK" };
    if (projected.length === 0 && this.mode === "single") {
      return {
        data: null,
        error: { message: "JSON object requested, multiple (or no) rows returned", code: "PGRST116", details: "", hint: "" },
        count,
        status: 406,
        statusText: "Not Acceptable",
      };
    }
    return { data: projected[0] ?? null, error: null, count, status: 200, statusText: "OK" };
  }

  then<R1 = Result, R2 = never>(
    onfulfilled?: ((value: Result) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: any) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2> {
    return Promise.resolve().then(() => this.run()).then(onfulfilled, onrejected);
  }
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

/** Edge functions that only read, so the dashboard can render around them. */
const READ_ONLY_FUNCTIONS = new Set([
  "workflow-definitions",
  "knowledge-library",
  "customer-graph-context",
  "customer-sales-intelligence",
  "data-source-fetch",
  "case-management",
  "complaint-connections",
  "business-data-connections",
  "collaborator-link-self",
  "mcp-fetch",
]);

const makeChannel = () => {
  const channel: any = {
    on: () => channel,
    subscribe: (callback?: (status: string) => void) => {
      callback?.("SUBSCRIBED");
      return channel;
    },
    unsubscribe: async () => "ok",
    send: async () => "ok",
  };
  return channel;
};

export const createDemoSupabaseClient = (store: DemoStore): any => {
  const user = DEMO_AUTH_USER;
  const session = {
    access_token: "demo-access-token",
    refresh_token: "demo-refresh-token",
    token_type: "bearer",
    expires_in: 60 * 60 * 24,
    expires_at: Math.floor(Date.now() / 1000) + 60 * 60 * 24,
    user,
  };

  return {
    from: (table: string) => new DemoQuery(table, store),
    rpc: (name: string) => {
      const query = new DemoQuery("__rpc__", store);
      if (/^(set|create|merge|update|delete)_/.test(name)) query.insert();
      return query;
    },
    functions: {
      invoke: async (name: string, options?: { body?: unknown }) => {
        const handler = store.functions?.[name];
        if (handler) {
          try {
            return { data: await handler(options?.body ?? {}), error: null };
          } catch (error) {
            notifyReadOnly();
            return { data: null, error: { ...readOnlyError(), message: error instanceof Error ? error.message : READ_ONLY_MESSAGE } };
          }
        }
        if (READ_ONLY_FUNCTIONS.has(name)) return { data: null, error: null };
        notifyReadOnly();
        return { data: null, error: { ...readOnlyError(), name: "FunctionsHttpError" } };
      },
    },
    auth: {
      getSession: async () => ({ data: { session }, error: null }),
      getUser: async () => ({ data: { user }, error: null }),
      refreshSession: async () => ({ data: { session, user }, error: null }),
      onAuthStateChange: (callback: (event: string, session: unknown) => void) => {
        setTimeout(() => callback("INITIAL_SESSION", session), 0);
        return { data: { subscription: { unsubscribe: () => undefined } } };
      },
      signOut: async () => {
        notifyReadOnly();
        return { error: null };
      },
      signInWithPassword: async () => ({ data: { user: null, session: null }, error: readOnlyError() }),
      signUp: async () => ({ data: { user: null, session: null }, error: readOnlyError() }),
      updateUser: async () => ({ data: { user }, error: readOnlyError() }),
      resetPasswordForEmail: async () => ({ data: {}, error: readOnlyError() }),
    },
    storage: {
      from: () => ({
        getPublicUrl: (path: string) => ({ data: { publicUrl: path } }),
        createSignedUrl: async (path: string) => ({ data: { signedUrl: path }, error: null }),
        createSignedUrls: async (paths: string[]) => ({
          data: paths.map((path) => ({ path, signedUrl: path, error: null })),
          error: null,
        }),
        list: async () => ({ data: [], error: null }),
        download: async () => ({ data: null, error: readOnlyError() }),
        upload: async () => ({ data: null, error: readOnlyError() }),
        remove: async () => ({ data: null, error: readOnlyError() }),
      }),
    },
    channel: () => makeChannel(),
    removeChannel: async () => "ok",
    removeAllChannels: async () => [],
    getChannels: () => [],
  };
};

export const DEMO_AUTH_USER = {
  id: DEMO_USER_ID,
  aud: "authenticated",
  role: "authenticated",
  email: "demo@resonance-music.example",
  app_metadata: { provider: "email", providers: ["email"] },
  user_metadata: {},
  created_at: new Date().toISOString(),
};
